import { useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  GetUsersForSchoolDocument,
  GetUsersForSchoolQuery,
  User,
  UserRoleEnum,
} from "../../../generated/graphql";
import { useRequestError } from "../../../hooks";
import { Autocomplete } from "@material-ui/lab";
import { Chip, IconButton } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import { ErrorMessageSnackbar } from "../../shared/ErrorMessageSnackbar";
import { LoadingProgress } from "../../shared/LoadingProgress";
import { StyledTextField } from "../Style";
import { mapUsersIds, findUserById } from "../utils";
import { QUERY_LIMIT } from "../constants";

interface SelectAdminsProps {
  admins: User[];
  inputError: boolean;
  setAdmins: (admins) => void;
}

const deleteIcon = (
  <IconButton>
    <CancelIcon />
  </IconButton>
);

export const SelectAdmins = ({ admins = [], inputError, setAdmins }: SelectAdminsProps) => {
  const { data, loading, error } = useQuery<GetUsersForSchoolQuery>(GetUsersForSchoolDocument, {
    variables: {
      getUsersData: {
        limit: QUERY_LIMIT,
        skip: 0,
        roles: [UserRoleEnum.Teacher],
      },
    },
  });

  const users = useMemo(() => data?.getUsers?.users as User[] ?? [], [data?.getUsers]);

  const getUserDataToRender = useCallback((userId: string) => {
    const { firstName, lastName, email } = findUserById(users, userId);
    return `${firstName} ${lastName} | ${email}`;
  }, [users]);

  const handleSetAdmins = (adminsIds: string[]) => {
    const adminsToSet = users.filter(({ id }) => adminsIds.includes(id));
    setAdmins(adminsToSet);
  };

  const handleRemoveAdmin = (adminId: string) => {
    const adminsToSet = admins.filter(({ id }) => id !== adminId);
    setAdmins(adminsToSet);
  }

  const { requestError, setRequestError, errorMessage } = useRequestError(
    error,
    "При загрузке списка пользователей произошла ошибка",
  );

  if (loading) {
    return <LoadingProgress />;
  }

  return (
    <>
      <Autocomplete
        multiple
        filterSelectedOptions
        options={mapUsersIds(users)}
        getOptionLabel={(userId) => getUserDataToRender(userId)}
        onChange={(_, value) => handleSetAdmins(value)}
        value={admins.map(({ id }) => id)}
        noOptionsText={"Администратор с данной почтой не найден"}
        renderTags={(value: string[], getTagProps) => {
          return value.map((tagId: string, index: number) => (
            <Chip
              variant="outlined"
              label={findUserById(admins, tagId)?.lastName}
              deleteIcon={deleteIcon}
              style={{ maxWidth: "200px" }}
              {...getTagProps({ index })}
              onDelete={() => handleRemoveAdmin(tagId)}
            />
          ));
        }}
        renderInput={(params) => (
          <StyledTextField
            label="Администраторы"
            variant="filled"
            error={inputError}
            {...params}
          />
        )}
      />

      <ErrorMessageSnackbar
        open={requestError}
        message={errorMessage}
        closeHandler={() => setRequestError(false)}
      />
    </>
  );
};
